import type { SchedulingIntakeReviewListFilter } from "@emr/contracts";
import type { createAgentClient } from "../../api/agentClient";
import type { HealthCardPhotosClient } from "../../api/healthCardPhotos";
import type { SchedulingIntakeClient } from "../../api/schedulingIntake";
import type { PatientProfileClient } from "../patient-profile/usePatientProfile";

/**
 * What the intake review needs: the intake routes plus the ordinary chart reads and editors, and
 * the patient directory when the client has it (the reconciler's "find the chart" search), and
 * the health card photo the patient sent from their link when it has those routes (PA-13).
 */
export type IntakeReviewClient = Pick<
  SchedulingIntakeClient,
  | "getSchedulingIntakeReview"
  | "getSchedulingPatientMatches"
  | "reconcileSchedulingPatient"
  | "applySchedulingIntakeDemographics"
  | "applySchedulingIntakeChartEntries"
  | "applySchedulingIntakeReason"
  | "markSchedulingIntakeSectionReviewed"
> &
  PatientProfileClient &
  Partial<Pick<ReturnType<typeof createAgentClient>, "listPatientDirectory">> &
  Partial<Pick<HealthCardPhotosClient, "listAppointmentHealthCardPhotos" | "openHealthCardPhoto">>;

export type IntakeReviewListClient = Pick<SchedulingIntakeClient, "listSchedulingIntakeReviews">;

export const schedulingIntakeKey = (siteId: string, appointmentId: string) =>
  ["scheduling-intake", siteId, appointmentId] as const;

export const schedulingPatientMatchesKey = (siteId: string, appointmentId: string) =>
  ["scheduling-patient-matches", siteId, appointmentId] as const;

export const schedulingIntakeListKey = (siteId: string, filter: SchedulingIntakeReviewListFilter) =>
  ["scheduling-intake-reviews", siteId, filter] as const;

/**
 * Cache rules for an audited read (the questionnaire, its chart matches, the review list): read
 * once each time the review or list opens, and again only after a change made here. Never on
 * window focus, a reconnect, or the 4-second schedule poll, and nothing is kept once it closes,
 * so the next opening shows what other staff applied meanwhile.
 */
export const AUDITED_READ = {
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: 0,
  refetchOnMount: "always",
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
} as const;
